import { useCallback } from 'react';
import { FormDataType } from '@tw/tw-component-library';
import { SectionKey } from '../../../../../types';
import { FormSchemaConfig, ValidationResult } from '../../types';
import { SCHEMA_ORDER } from '../schemas/schemaOrder';
import { createLogger } from '../../../../../utils/logger';

const logger = createLogger('useHandleValidation');

interface ValidationDependencies {
  formConfig: any;
  currentSection: SectionKey | null;
  formData: FormDataType | null;
  currentSchema: FormSchemaConfig | null;
  validateSection: (
    schema: FormSchemaConfig,
    data: FormDataType
  ) => ValidationResult;
  updateSectionProgress?: (
    sectionKey: SectionKey,
    isComplete: boolean,
    completedDate?: string
  ) => void;
  setErrors?: (errors: Record<string, string>) => void;
  formSchemas: Record<SectionKey, FormSchemaConfig>;
}

export const useHandleValidation = (deps: ValidationDependencies) => {
  const {
    formConfig,
    currentSection,
    formData,
    currentSchema,
    validateSection,
    updateSectionProgress,
    setErrors,
    formSchemas,
  } = deps;

  const debugLog = (message: string, data: any) => {
    if (formConfig.debugMode) {
      logger.state(message, data);
    }
  };

  return useCallback(() => {
    if (!formConfig.enableValidation) {
      debugLog('Validation skipped - disabled in config', { currentSection });
      return;
    }

    if (!currentSection || !formData || !currentSchema) return;

    // Special handling for reassignment
    if (currentSection === 'request_reassign') {
      const validation = validateSection(currentSchema, formData);

      if (validation.isValid && updateSectionProgress) {
        // Reset progress of affected sections
        SCHEMA_ORDER.request_reassign.affectedSections?.forEach((section) => {
          updateSectionProgress(section, false);
        });
      }

      if (setErrors) {
        setErrors(
          validation.errors.reduce<Record<string, string>>(
            (acc, error) => ({ ...acc, [error.field]: error.message }),
            {}
          )
        );
      }
      return;
    }

    // Regular validation logic
    const dependentSections = Object.entries(SCHEMA_ORDER)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([section, config]) =>
        config.dependencies?.includes(currentSection)
      )
      .map(([section]) => section as SectionKey);

    const validation = validateSection(currentSchema, formData);
    const dependentValidations = dependentSections.map((section) => {
      const schema = formSchemas[section];
      return validateSection(schema, formData);
    });

    if (updateSectionProgress) {
      updateSectionProgress(currentSection, validation.isValid);
      dependentSections.forEach((section, index) => {
        updateSectionProgress(section, dependentValidations[index].isValid);
      });
    }

    if (setErrors) {
      const allErrors = [validation, ...dependentValidations]
        .flatMap((v) => v.errors)
        .reduce<Record<string, string>>(
          (acc, error) => ({ ...acc, [error.field]: error.message }),
          {}
        );
      setErrors(allErrors);
    }
  }, [
    formConfig,
    currentSection,
    formData,
    currentSchema,
    validateSection,
    updateSectionProgress,
    setErrors,
    formSchemas,
  ]);
};
